import type { C1_AxisData, WithOptionalId } from "../types/C1_ModelTypes";
import BaseModel from "../../../src/models/BaseModel";
import ObjectFactory from "../../../src/models/ObjectFactory";
import { floorsForAxis } from "./C1_axisFloorScope";
import { c1_state } from "./C1_runtimeContext";

export class C1_Axis extends BaseModel {
  get className(): string {
    return "C1_Axis";
  }

  override getPersistentProperties(): string[] {
    return ["data", ...super.getPersistentProperties()];
  }

  data: C1_AxisData;

  constructor(data: Partial<WithOptionalId<C1_AxisData>> = {}, parent?: BaseModel) {
    super(undefined, parent);
    const { id: _legacyId, ...axisData } = data;
    this.data = Object.assign(
      {
        id: "",
        kind: "v" as const,
        pos: 0,
        label: "",
        locked: true,
      } satisfies C1_AxisData,
      axisData,
      { locked: axisData.locked ?? true },
    );
  }

  get floorId() { return this.data.floorId; }

  setFloorId(floorId: string | undefined): void {
    if (this.data.floorId === floorId) return;
    this.beforeChange("floorId");
    this.data.floorId = floorId;
    this.changed("floorId");
  }

  get kind() {
    return this.data.kind;
  }

  get pos() {
    return this.data.pos;
  }

  get label() {
    return this.data.label;
  }

  get locked() {
    return this.data.locked;
  }

  setPosition(pos: number) {
    if (pos !== this.data.pos) {
      if (state.settings.floorsHaveIndividualAxes && !this.floorId) {
        c1_state.project.isolateAxisForFloor(this, c1_state.currentFloorId.value);
      }
      const affectedFloors = floorsForAxis(this, c1_state.project.floors, state.settings.floorsHaveIndividualAxes);
      const previousPosition = this.data.pos;
      this.beforeChange("pos");
      this.data.pos = pos;
      for (const floor of affectedFloors) {
        for (const room of floor.rooms) room.moveAlignedPoints(this.kind, previousPosition, pos);
      }
      this.changed("pos");
    }
  }

  setLabel(label: string) {
    if (label !== this.data.label) {
      this.beforeChange("label");
      this.data.label = label;
      this.changed("label");
    }
  }

  setLocked(locked: boolean) {
    if (locked !== this.data.locked) {
      this.beforeChange("locked");
      this.data.locked = locked;
      this.changed("locked");
    }
  }

  toJSON(): C1_AxisData {
    return { ...this.data, id: this.id };
  }
}

ObjectFactory.registerClass("C1_Axis", (data, parent) => new C1_Axis(data, parent));
